import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ScrollText, Loader2, AlertCircle, Search, X, Filter } from 'lucide-react';
import { getGlobalAudit, type AuditEvent } from '../api/client';

const EVENT_TYPE_GROUPS: { label: string; values: string[] }[] = [
  { label: 'All', values: [] },
  {
    label: 'Users',
    values: ['user.create', 'user.update', 'user.deactivate', 'user.reactivate', 'user.delete'],
  },
  {
    label: 'Subscriptions',
    values: [
      'subscription.cancel',
      'subscription.update',
      'subscription.payment_failed',
      'subscription.payment_succeeded',
    ],
  },
  {
    label: 'Auth',
    values: ['auth.password_reset', 'auth.password_reset_request', 'auth.email_verify'],
  },
];

export function AdminAudit() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groupFilter, setGroupFilter] = useState<string>('All');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getGlobalAudit(200)
      .then((rows) => {
        if (!cancelled) setEvents(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        setError((err as Error).message || 'Failed to load audit events');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const allowed = EVENT_TYPE_GROUPS.find((g) => g.label === groupFilter)?.values ?? [];
    return events.filter((e) => {
      if (allowed.length > 0 && !allowed.includes(e.event_type)) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const haystack = `${e.event_type} ${e.message ?? ''} ${e.actor_email ?? ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [events, groupFilter, search]);

  return (
    <div>
      <div className="mb-6">
        <Link
          to="/"
          className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Dashboard
        </Link>
        <div className="flex items-center gap-3">
          <ScrollText className="w-8 h-8 text-brand-orange" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Audit Log</h1>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Recent admin and user actions. Append-only — no edits or deletes.
        </p>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search events…"
            className="w-full pl-9 pr-9 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent text-sm"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 text-sm">
          <Filter className="w-4 h-4 text-gray-400" />
          {EVENT_TYPE_GROUPS.map((g) => (
            <button
              key={g.label}
              onClick={() => setGroupFilter(g.label)}
              className={`px-3 py-1 rounded-lg text-xs font-medium ${
                groupFilter === g.label
                  ? 'bg-ink text-on-ink'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900 text-xs uppercase text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Time</th>
                <th className="px-4 py-2 text-left font-medium">Event</th>
                <th className="px-4 py-2 text-left font-medium">Actor</th>
                <th className="px-4 py-2 text-left font-medium">Target</th>
                <th className="px-4 py-2 text-left font-medium">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    No events match your filters.
                  </td>
                </tr>
              ) : (
                filtered.map((e) => (
                  <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      {new Date(e.created_at).toLocaleString('en-AU')}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-900 dark:text-white whitespace-nowrap">
                      {e.event_type}
                    </td>
                    <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                      {e.actor_email || (
                        <span className="text-gray-400 italic">system</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                      {e.target_user_id && (
                        <span className="font-mono text-xs">user:{e.target_user_id.slice(0, 8)}</span>
                      )}
                      {e.target_site_id && (
                        <span className="font-mono text-xs ml-1">
                          site:{e.target_site_id.slice(0, 8)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                      <div>{e.message}</div>
                      {e.data && Object.keys(e.data).length > 0 && (
                        <details className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          <summary className="cursor-pointer">data</summary>
                          <pre className="mt-1 p-2 bg-gray-50 dark:bg-gray-900 rounded font-mono overflow-x-auto">
                            {JSON.stringify(e.data, null, 2)}
                          </pre>
                        </details>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
